// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: a small floating menu for the scratchpad, opened at a point (right-click) or
// under an element (chip click). Same look as the "/" menu. Arrow keys, Enter, Escape.

import styles from '@app/features/konvo_scratchpad/components/ScratchpadDrawer.module.css';
import {clsx} from 'clsx';
import type React from 'react';
import {useEffect, useLayoutEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';

export interface PopMenuItem {
	key: string;
	label: string;
	hint?: string;
	icon?: React.ReactNode;
	danger?: boolean;
	disabled?: boolean;
	/** Items with children open a second column of choices. */
	children?: Array<PopMenuItem>;
	onSelect?: () => void;
}

export interface PopMenuAnchor {
	x: number;
	y: number;
}

interface PopMenuProps {
	anchor: PopMenuAnchor;
	items: Array<PopMenuItem>;
	onClose: () => void;
	/** Custom content instead of items, e.g. a picker. */
	children?: React.ReactNode;
	width?: number;
	/** 'above' grows the menu upwards from the anchor (menus opened from a bar at the bottom). */
	placement?: 'below' | 'above';
}

/** Keeps the menu inside the viewport. */
function usePlacement(
	anchor: PopMenuAnchor,
	ref: React.RefObject<HTMLDivElement | null>,
	placement: 'below' | 'above',
): PopMenuAnchor {
	const [placed, setPlaced] = useState(anchor);
	useLayoutEffect(() => {
		const element = ref.current;
		if (!element) return;
		const rect = element.getBoundingClientRect();
		const x = Math.min(anchor.x, window.innerWidth - rect.width - 8);
		const y =
			placement === 'above'
				? Math.max(8, anchor.y - rect.height)
				: anchor.y + rect.height > window.innerHeight - 8
					? Math.max(8, anchor.y - rect.height)
					: anchor.y;
		setPlaced({x: Math.max(8, x), y});
	}, [anchor, ref, placement]);
	return placed;
}

export function PopMenu({anchor, items, onClose, children, width, placement = 'below'}: PopMenuProps) {
	const ref = useRef<HTMLDivElement>(null);
	const placed = usePlacement(anchor, ref, placement);
	const [index, setIndex] = useState(0);
	const [open, setOpen] = useState<string | null>(null);

	useEffect(() => {
		const onDown = (event: PointerEvent) => {
			if (ref.current && event.target instanceof Node && !ref.current.contains(event.target)) onClose();
		};
		const onKey = (event: KeyboardEvent) => {
			if (event.key === 'Escape') {
				// Ours to close; the drawer must not close with it.
				event.preventDefault();
				event.stopPropagation();
				onClose();
				return;
			}
			if (children) return;
			const list = open ? (items.find((item) => item.key === open)?.children ?? []) : items;
			if (event.key === 'ArrowDown') {
				event.preventDefault();
				setIndex((value) => Math.min(list.length - 1, value + 1));
			} else if (event.key === 'ArrowUp') {
				event.preventDefault();
				setIndex((value) => Math.max(0, value - 1));
			} else if (event.key === 'Enter') {
				event.preventDefault();
				const item = list[index];
				if (!item || item.disabled) return;
				if (item.children) {
					setOpen(item.key);
					setIndex(0);
				} else {
					item.onSelect?.();
					onClose();
				}
			} else if (event.key === 'ArrowLeft' && open) {
				setOpen(null);
				setIndex(0);
			}
		};
		window.addEventListener('pointerdown', onDown, true);
		window.addEventListener('keydown', onKey, true);
		return () => {
			window.removeEventListener('pointerdown', onDown, true);
			window.removeEventListener('keydown', onKey, true);
		};
	}, [items, index, open, onClose, children]);

	const list = open ? (items.find((item) => item.key === open)?.children ?? []) : items;
	return createPortal(
		<div
			ref={ref}
			className={clsx(styles.menu, styles.popMenu)}
			style={{top: placed.y, left: placed.x, width, visibility: placed === anchor ? 'hidden' : 'visible'}}
			role={children ? 'dialog' : 'menu'}
			data-flx="konvo-scratchpad.pop-menu"
		>
			{children ??
				list.map((item, itemIndex) => (
					<button
						key={item.key}
						type="button"
						role="menuitem"
						disabled={item.disabled}
						className={clsx(
							styles.menuItem,
							styles.popMenuItem,
							itemIndex === index && styles.menuItemActive,
							item.danger && styles.menuItemDanger,
						)}
						onMouseEnter={() => setIndex(itemIndex)}
						onMouseDown={(event) => event.preventDefault()}
						onClick={() => {
							if (item.children) {
								setOpen(item.key);
								setIndex(0);
								return;
							}
							item.onSelect?.();
							onClose();
						}}
						data-flx="konvo-scratchpad.pop-menu-item"
					>
						{item.icon && <span className={styles.menuIcon}>{item.icon}</span>}
						<span className={styles.menuText}>
							<span className={styles.menuLabel}>{item.label}</span>
							{item.hint && <span className={styles.menuHint}>{item.hint}</span>}
						</span>
						{item.children && <span className={styles.menuChevron}>›</span>}
					</button>
				))}
		</div>,
		document.body,
	);
}
